/**
 * useRiskAssessmentConfig.js
 *
 * Hook for managing vessel-specific risk assessment configuration settings.
 * ISM Code / WBC3 Edition: 7 settings for risk assessment management
 *
 * Settings:
 * 1. Default Review Frequency - annually, biannually, quarterly, monthly
 * 2. Due Soon Warning Days - Days before review to show "Due Soon" (7-90)
 * 3. Show ALARP Guidance - Display ALARP zone guidance on assessments
 * 4. Require Action Plan for High Risk - Enforce for ALARP/Intolerable zones (ISM Code 1.2.2.2)
 * 5. Show Risk Matrix on PDF - Include risk matrix in PDF exports
 * 6. Show Control Hierarchy on PDF - Include control hierarchy chart in exports
 * 7. Require Digital Signatures - Require signatures for approval (ISM Code 12.2)
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';

// RBAC helper (same pattern as other config hooks)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Review frequency options
 */
export const REVIEW_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly', months: 1, description: 'For high-risk activities' },
  { value: 'quarterly', label: 'Quarterly', months: 3, description: 'Review every 3 months' },
  { value: 'biannually', label: 'Bi-annually', months: 6, description: 'Review every 6 months' },
  { value: 'annually', label: 'Annually', months: 12, description: 'Most common (recommended)' },
  { value: 'biennial', label: 'Biennial', months: 24, description: 'Review every 2 years' }
];

/**
 * Default risk assessment configuration
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Setting 1: Default review frequency for new assessments
  defaultReviewFrequency: 'annually',

  // Setting 2: Days before review to show "Due Soon" warning
  dueSoonWarningDays: 30,

  // Setting 3: Show ALARP guidance on risk assessments
  showAlarpGuidance: true,

  // Setting 4: Require action plan for high-risk assessments (ISM Code 1.2.2.2)
  requireActionPlanForHighRisk: true,

  // Setting 5: Include risk matrix in PDF exports
  showRiskMatrixOnPdf: true,

  // Setting 6: Include control hierarchy chart in PDF exports
  showControlHierarchyOnPdf: true,

  // Setting 7: Require digital signatures for approval (ISM Code 12.2)
  requireDigitalSignatures: true,

  // Metadata
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * Hook for managing risk assessment configuration
 */
export const useRiskAssessmentConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage
  const storageKey = 'risk_assessment_config';
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    storageKey,
    getDefaultConfig()
  );

  // Ensure config has all required fields (handle upgrades)
  const config = useMemo(() => {
    const defaults = getDefaultConfig();
    return {
      ...defaults,
      ...rawConfig
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get review frequency details
   */
  const reviewFrequencyDetails = useMemo(() => {
    return REVIEW_FREQUENCY_OPTIONS.find(opt => opt.value === config.defaultReviewFrequency) ||
      REVIEW_FREQUENCY_OPTIONS.find(opt => opt.value === 'annually');
  }, [config.defaultReviewFrequency]);

  /**
   * Check if any regulatory settings are disabled
   */
  const disabledRegulatorySettings = useMemo(() => {
    const disabled = [];
    if (!config.requireActionPlanForHighRisk) {
      disabled.push('Require Action Plan for High Risk');
    }
    if (!config.requireDigitalSignatures) {
      disabled.push('Require Digital Signatures');
    }
    return disabled;
  }, [config.requireActionPlanForHighRisk, config.requireDigitalSignatures]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useRiskAssessmentConfig] Permission denied');
      return false;
    }

    const oldValue = config[settingKey];

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      [settingKey]: value,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.UPDATE,
        'risk_assessment_config',
        {
          vesselId: currentVessel?.id,
          setting: settingKey,
          newValue: value,
          oldValue
        },
        { userId: currentUser?.id, name: currentUser?.name }
      );
    } catch (e) {
      console.warn('[useRiskAssessmentConfig] Audit log failed:', e);
    }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Set default review frequency
   */
  const setDefaultReviewFrequency = useCallback(async (frequency) => {
    const valid = REVIEW_FREQUENCY_OPTIONS.some(opt => opt.value === frequency);
    if (!valid) {
      console.warn('[useRiskAssessmentConfig] Invalid frequency:', frequency);
      return false;
    }
    return updateSetting('defaultReviewFrequency', frequency);
  }, [updateSetting]);

  /**
   * Set due soon warning days
   */
  const setDueSoonWarningDays = useCallback(async (days) => {
    const clamped = Math.min(90, Math.max(7, days));
    return updateSetting('dueSoonWarningDays', clamped);
  }, [updateSetting]);

  /**
   * Toggle show ALARP guidance
   */
  const setShowAlarpGuidance = useCallback(async (show) => {
    return updateSetting('showAlarpGuidance', !!show);
  }, [updateSetting]);

  /**
   * Toggle require action plan for high risk
   */
  const setRequireActionPlanForHighRisk = useCallback(async (require) => {
    return updateSetting('requireActionPlanForHighRisk', !!require);
  }, [updateSetting]);

  /**
   * Toggle show risk matrix on PDF
   */
  const setShowRiskMatrixOnPdf = useCallback(async (show) => {
    return updateSetting('showRiskMatrixOnPdf', !!show);
  }, [updateSetting]);

  /**
   * Toggle show control hierarchy on PDF
   */
  const setShowControlHierarchyOnPdf = useCallback(async (show) => {
    return updateSetting('showControlHierarchyOnPdf', !!show);
  }, [updateSetting]);

  /**
   * Toggle require digital signatures
   */
  const setRequireDigitalSignatures = useCallback(async (require) => {
    return updateSetting('requireDigitalSignatures', !!require);
  }, [updateSetting]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useRiskAssessmentConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.UPDATE,
        'risk_assessment_config',
        { vesselId: currentVessel?.id, action: 'reset_to_defaults' },
        { userId: currentUser?.id, name: currentUser?.name }
      );
    } catch (e) {
      console.warn('[useRiskAssessmentConfig] Audit log failed:', e);
    }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // Computed values
    reviewFrequencyDetails,
    disabledRegulatorySettings,

    // Direct settings access
    defaultReviewFrequency: config.defaultReviewFrequency,
    dueSoonWarningDays: config.dueSoonWarningDays,
    showAlarpGuidance: config.showAlarpGuidance,
    requireActionPlanForHighRisk: config.requireActionPlanForHighRisk,
    showRiskMatrixOnPdf: config.showRiskMatrixOnPdf,
    showControlHierarchyOnPdf: config.showControlHierarchyOnPdf,
    requireDigitalSignatures: config.requireDigitalSignatures,

    // Individual mutations
    setDefaultReviewFrequency,
    setDueSoonWarningDays,
    setShowAlarpGuidance,
    setRequireActionPlanForHighRisk,
    setShowRiskMatrixOnPdf,
    setShowControlHierarchyOnPdf,
    setRequireDigitalSignatures,
    resetToDefaults,

    // Generic update
    updateSetting,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    REVIEW_FREQUENCY_OPTIONS
  };
};

export default useRiskAssessmentConfig;
